import { format } from 'date-fns';
import { useState } from 'react';

import { Callout } from '../../cdl/Callout/Callout';
import { Label } from '../../cdl/Label/Label';
import { Description, Radio, RadioGroup, RadioGroupItem } from '../../cdl/RadioGroup/RadioGroup';
import { Text } from '../../cdl/Text/Text';
import { Divider } from '../../common/Divider/Divider';
import { translate } from '../../common/helpers/translate.helper';
import { IconLock } from '../../common/icons/cdl/Lock';
import { Box } from '../../common/ui/Box';
import { DemandDataBasis } from '../types/DemandDataBasis';

interface DemandDataFreezeControlProps {
    dataBasis: DemandDataBasis;
    dateFrozen?: Date;
    isDisabled: boolean;
    isStaticDisabled: boolean;
    onDataBasisChange: (dataBasis: DemandDataBasis) => Promise<void>;
}

export const DemandDataFreezeControl = ({
    dataBasis,
    dateFrozen,
    isDisabled,
    isStaticDisabled,
    onDataBasisChange,
}: DemandDataFreezeControlProps) => {
    const [pendingValue, setPendingValue] = useState<DemandDataBasis>();
    const changeValue = async (value: DemandDataBasis) => {
        setPendingValue(value);
        await onDataBasisChange(value);
        setPendingValue(undefined);
    };

    return (
        <Box display="flex" flexDirection="column" gap={16}>
            <Divider />
            <Box display="flex" flexDirection="column" gap={4}>
                <Text as="h2" variant="subtitle1">
                    {translate('demandDefinition.dataBasis')}
                </Text>
                <Text variant="small" color="foreground.muted">
                    {translate('demandDefinition.dataBasisDescription')}
                </Text>
            </Box>
            <RadioGroup
                value={pendingValue ?? dataBasis}
                disabled={isDisabled || pendingValue !== undefined}
                onValueChange={changeValue}
            >
                <RadioGroupItem>
                    <Radio value={DemandDataBasis.DYNAMIC} />
                    <Label>{translate('demandDefinition.dynamicData')}</Label>
                    <Description>{translate('demandDefinition.dynamicDataDescription')}</Description>
                </RadioGroupItem>
                <RadioGroupItem>
                    <Radio
                        value={DemandDataBasis.STATIC}
                        disabled={dataBasis === DemandDataBasis.DYNAMIC && isStaticDisabled}
                    />
                    <Label>{translate('demandDefinition.staticData')}</Label>
                    <Description>{translate('demandDefinition.staticDataDescription')}</Description>
                </RadioGroupItem>
            </RadioGroup>
            {dateFrozen ? (
                <Callout
                    variant="attention"
                    Icon={IconLock}
                    description={translate('demandDefinition.staticSince', {
                        date: format(dateFrozen, 'MMM d, yyyy'),
                    })}
                />
            ) : null}
        </Box>
    );
};
